import axios from 'axios';
import { 
  Customer, 
  CustomerCreateData, 
  CustomersResponse, 
  CustomerFilters,
  CustomerStats 
} from '@/types/customer';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface GetCustomersParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  active?: string;
}

class CustomerService {
  async getCustomers(params: GetCustomersParams = {}): Promise<CustomersResponse> {
    console.log('📋 CustomerService: Getting customers', params);
    try {
      const response = await axios.get(`${API_URL}/customers`, { params });
      console.log('✅ CustomerService: Customers retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CustomerService: Error getting customers', error.response?.data || error.message);
      throw error;
    }
  }

  async getCustomerById(id: number): Promise<Customer> {
    console.log('👤 CustomerService: Getting customer by ID', { id });
    try {
      const response = await axios.get(`${API_URL}/customers/${id}`);
      console.log('✅ CustomerService: Customer retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CustomerService: Error getting customer by ID', error.response?.data || error.message);
      throw error;
    }
  }

  async createCustomer(customerData: CustomerCreateData): Promise<Customer> {
    console.log('➕ CustomerService: Creating customer', { name: customerData.name, type: customerData.type });
    try {
      const response = await axios.post(`${API_URL}/customers`, customerData);
      console.log('✅ CustomerService: Customer created', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CustomerService: Error creating customer', error.response?.data || error.message);
      throw error;
    }
  }

  async updateCustomer(id: number, customerData: Partial<CustomerCreateData>): Promise<Customer> {
    console.log('✏️ CustomerService: Updating customer', { id, name: customerData.name });
    try {
      const response = await axios.put(`${API_URL}/customers/${id}`, customerData);
      console.log('✅ CustomerService: Customer updated', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CustomerService: Error updating customer', error.response?.data || error.message);
      throw error;
    }
  }

  async deleteCustomer(id: number): Promise<void> {
    console.log('🗑️ CustomerService: Deleting customer', { id });
    try {
      const response = await axios.delete(`${API_URL}/customers/${id}`);
      console.log('✅ CustomerService: Customer deleted', response.data);
    } catch (error: any) {
      console.log('❌ CustomerService: Error deleting customer', error.response?.data || error.message);
      throw error;
    }
  }

  async toggleCustomerStatus(id: number): Promise<Customer> {
    console.log('🔄 CustomerService: Toggling customer status', { id });
    try {
      const response = await axios.patch(`${API_URL}/customers/${id}/toggle-active`);
      console.log('✅ CustomerService: Customer status toggled', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CustomerService: Error toggling customer status', error.response?.data || error.message);
      throw error;
    }
  }

  async searchCustomers(query: string, limit: number = 10): Promise<Customer[]> {
    console.log('🔍 CustomerService: Searching customers', { query, limit });
    try {
      const response = await axios.get(`${API_URL}/customers`, {
        params: { search: query, limit, active: 'true' }
      });
      console.log('✅ CustomerService: Customer search completed', response.data);
      return response.data.data.customers;
    } catch (error: any) {
      console.log('❌ CustomerService: Error searching customers', error.response?.data || error.message);
      throw error;
    }
  }

  async getStats(): Promise<CustomerStats> {
    console.log('📊 CustomerService: Getting customer statistics');
    try {
      const response = await axios.get(`${API_URL}/customers/stats`);
      console.log('✅ CustomerService: Customer stats retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CustomerService: Error getting customer stats', error.response?.data || error.message);
      throw error;
    }
  }

  // Helper methods for form validation
  validateCustomerData(customerData: CustomerCreateData): string[] {
    const errors: string[] = [];

    if (!customerData.name?.trim()) {
      errors.push('El nombre es requerido');
    }

    if (!customerData.type) {
      errors.push('El tipo de cliente es requerido');
    }

    if (!customerData.doc_number?.trim()) {
      errors.push('El número de documento es requerido');
    }

    if (customerData.email && !this.isValidEmail(customerData.email)) {
      errors.push('El formato del email no es válido');
    }

    if (customerData.phone && !this.isValidPhone(customerData.phone)) {
      errors.push('El formato del teléfono no es válido');
    }

    return errors;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private isValidPhone(phone: string): boolean {
    // Basic phone validation - adjust regex based on your requirements
    const phoneRegex = /^[\+\-\s\(\)\d]{8,20}$/;
    return phoneRegex.test(phone);
  }

  // Format customer data for display
  formatCustomerType(type: 'NATURAL' | 'JURIDICA'): string {
    return type === 'NATURAL' ? 'Persona Natural' : 'Persona Jurídica';
  }

  formatCustomerStatus(active: boolean): string {
    return active ? 'Activo' : 'Inactivo';
  }

  // Filter and sort utilities
  filterCustomers(customers: Customer[], filters: CustomerFilters): Customer[] {
    return customers.filter(customer => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          customer.name.toLowerCase().includes(searchLower) ||
          customer.email?.toLowerCase().includes(searchLower) ||
          customer.doc_number.toLowerCase().includes(searchLower) ||
          customer.phone?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      if (filters.type) {
        if (customer.type !== filters.type) return false;
      }

      if (filters.active !== undefined) {
        if (customer.active !== filters.active) return false;
      }

      return true;
    });
  }

  sortCustomers(customers: Customer[], sortBy: keyof Customer, sortOrder: 'asc' | 'desc' = 'asc'): Customer[] {
    return [...customers].sort((a, b) => {
      const aVal = String(a[sortBy] ?? '');
      const bVal = String(b[sortBy] ?? '');

      if (aVal === bVal) return 0;

      const result = aVal < bVal ? -1 : 1;
      return sortOrder === 'asc' ? result : -result;
    });
  }
}

export const customerService = new CustomerService();